import { API_BASE_URL } from '../config'

export type SignatureFieldType = 'signature' | 'initial' | 'date' | 'text' | 'paragraph'

export interface NewSignatureField {
  type: SignatureFieldType
  page: number // 0-based
  xPct: number
  yPct: number
  widthPct: number
  heightPct: number
}

export interface SignatureField extends NewSignatureField {
  id: string
}

export interface SignedFieldValue {
  fieldId: string
  // base64 PNG (data URL) for signature/initial fields, plain text for date/text fields
  value: string
}

export interface SignRequest {
  fileName: string
  signerName: string
  signerEmail: string
  fields: SignatureField[]
  documentUrl: string
  // false if an earlier signer in the sequence hasn't finished yet — the
  // link/document can still be previewed, but nothing can be filled in or
  // submitted until it's this signer's turn (see backend/src/esign.ts).
  isYourTurn: boolean
  // The conveyancing firm's name, and the specific person (falls back to
  // their email) who actually sent this — either can be null if it
  // couldn't be resolved. Shown so the signer knows who's asking.
  requestedByFirm: string | null
  requestedByPerson: string | null
  // Optional per-tenant look for this page (see SettingsPage.tsx) — every
  // field is null when the tenant hasn't set it, and the page keeps its own
  // default Conveyo.One look in that case.
  branding: {
    logoUrl: string | null
    brandColor: string | null
    fontFamily: string | null
  }
}

// Public: the signer never logs in — the unguessable token in the URL is the
// only credential, same trust model as matter-upload-links/client-invites
// (see backend/src/esign.ts).
export const getSignRequest = async (token: string): Promise<SignRequest> => {
  const response = await fetch(`${API_BASE_URL}/sign/${token}`)
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `This signing link is not valid (${response.status})`)
  }

  return data
}

export const submitSignature = async (
  token: string,
  signedFields: SignedFieldValue[],
): Promise<{ status: string; envelopeStatus: 'sent' | 'completed' }> => {
  const response = await fetch(`${API_BASE_URL}/sign/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signedFields }),
  })
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `Failed to submit signature (${response.status})`)
  }

  return data
}
